import { FiChevronRight } from "react-icons/fi";

import {
  MdStorage,
  MdDns,
  MdHub,
  MdTrendingUp,
  MdCheckCircle,
  MdAutoGraph
} from "react-icons/md";

export function renderDatabaseMonitoring() {
  return (
    <div className="transition-opacity duration-300">

      {/* Breadcrumbs */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] dark:text-[#c9a37c] mb-8">
        <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
        <FiChevronRight className="text-[14px]" />
        <span>Product Capabilities</span>
        <FiChevronRight className="text-[14px]" />
        <span className="text-[#1b140d] dark:text-white font-medium">Database Monitoring</span>
      </nav>

      {/* Page Title */}
      <h1 className="text-5xl md:text-5xl font-bold text-[#1b140d] dark:text-white tracking-tight mb-4">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
          Database Monitoring
        </span>
        <span className="text-orange-500">:</span><br />
        Visibility into Data Stores
      </h1>

      {/* Introduction */}
      <p className="text-lg text-text-secondary dark:text-slate-400 leading-relaxed mb-10">
        Databases are critical dependencies for any modern application. Bottlenecks in the data layer often cascade into user-facing latency. Netraa's deep observability approach ensures you have granular visibility into every query, transaction, and connection pool event, allowing you to optimize performance proactively.
      </p>

      {/* Note / Prerequisite */}
      <div className="flex gap-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 animate-fadeIn">
        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 mt-0.5">info</span>
        <div>
          <h4 className="text-sm font-bold text-blue-900 dark:text-blue-200 mb-1">Prerequisite</h4>
          <p className="text-sm text-blue-800 dark:text-blue-300 leading-normal">
            To enable detailed query analysis, ensure the Netraa Agent version 2.1+ is installed on your database host or configured via the sidecar pattern.
          </p>
        </div>
      </div>

      {/* MySQL Monitoring Section */}
      <section id="mysql-monitoring" className="scroll-mt-24 mt-6 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="size-10 rounded-lg bg-[#00758f]/10 dark:bg-[#00758f]/20 flex items-center justify-center text-[#00758f] transition-transform duration-300 hover:scale-105">
            <MdStorage className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-text-main dark:text-white">MySQL Monitoring</h2>
        </div>
        <p className="text-text-secondary dark:text-slate-400 mb-6">
          Gain deep insights into your MySQL clusters. Netraa automatically detects MySQL instances and begins collecting high-fidelity telemetry immediately upon agent startup.
        </p>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="animate-fadeIn">
            <h3 className="text-lg font-bold text-text-main dark:text-white mb-2">Query Performance</h3>
            <p className="text-sm text-text-secondary dark:text-slate-400">
              Identify slow queries automatically captured for latency spikes &gt; 500ms.
            </p>
          </div>
          <div className="animate-fadeIn delay-150">
            <h3 className="text-lg font-bold text-text-main dark:text-white mb-2">Instance Metrics</h3>
            <p className="text-sm text-text-secondary dark:text-slate-400">
              Monitor buffer pool usage, thread cache hits, and open file limits to prevent resource exhaustion.
            </p>
          </div>
        </div>

        {/* MySQL Dashboard Mock */}
        <figure className="rounded-xl border border-border-subtle dark:border-slate-700 bg-surface-light dark:bg-slate-900 overflow-hidden shadow-sm mb-4 group hover:shadow-lg transition-shadow">
          <div className="bg-surface-light dark:bg-slate-900 border-b border-border-subtle dark:border-slate-700 px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-bold text-text-secondary dark:text-slate-400 uppercase tracking-wider">MySQL Performance Dashboard</span>
            <div className="flex gap-2">
              <span className="size-2 rounded-full bg-red-400 animate-pulse"></span>
              <span className="text-xs font-medium text-text-secondary dark:text-slate-400">Live</span>
            </div>
          </div>

          <div className="p-6 bg-[#fafafa] dark:bg-slate-800">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Chart Area */}
              <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-4 rounded-lg border border-border-subtle dark:border-slate-700 shadow-sm animate-fadeIn">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-bold text-text-main dark:text-white">Transactions per Second (TPS)</h4>
                  <span className="text-xs text-text-secondary dark:text-slate-400">Last 1h</span>
                </div>
                <div aria-hidden="true" className="h-32 w-full flex items-end gap-1">
                  <div className="w-full h-full relative overflow-hidden">
                    <svg className="w-full h-full text-primary" preserveAspectRatio="none" viewBox="0 0 400 100">
                      <defs>
                        <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="currentColor" stopOpacity="0.2"></stop>
                          <stop offset="100%" stopColor="currentColor" stopOpacity="0"></stop>
                        </linearGradient>
                      </defs>
                      <path d="M0,80 C20,75 40,85 60,60 C80,35 100,40 120,50 C140,60 160,30 180,40 C200,50 220,55 240,35 C260,15 280,25 300,40 C320,55 340,45 360,50 C380,55 400,30 400,30 V100 H0 Z" fill="url(#gradient)"></path>
                      <path d="M0,80 C20,75 40,85 60,60 C80,35 100,40 120,50 C140,60 160,30 180,40 C200,50 220,55 240,35 C260,15 280,25 300,40 C320,55 340,45 360,50 C380,55 400,30 400,30" fill="none" stroke="currentColor" strokeWidth="2"></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Stats Area */}
              <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-border-subtle dark:border-slate-700 shadow-sm flex flex-col justify-center animate-fadeIn delay-150">
                <span className="text-xs text-text-secondary dark:text-slate-400 font-medium mb-1">Avg Query Latency</span>
                <div className="text-3xl font-bold text-text-main dark:text-white mb-4">4.2ms</div>
                <span className="text-xs text-text-secondary dark:text-slate-400 font-medium mb-1">Active Threads</span>
                <div className="text-3xl font-bold text-text-main dark:text-white">128</div>
              </div>
            </div>

            {/* Slow Query Table */}
            <div className="mt-6 bg-white dark:bg-slate-900 rounded-lg border border-border-subtle dark:border-slate-700 shadow-sm overflow-hidden animate-fadeIn delay-200">
              <div className="px-4 py-3 border-b border-border-subtle dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
                <h4 className="text-sm font-bold text-text-main dark:text-white">Slow Query Log</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-text-secondary dark:text-slate-400 uppercase bg-gray-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 font-medium">Query Snippet</th>
                      <th className="px-4 py-3 font-medium">Duration</th>
                      <th className="px-4 py-3 font-medium">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle dark:divide-slate-700">
                    <tr className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-text-main dark:text-white truncate max-w-[200px]">
                        SELECT * FROM users WHERE last_login &lt;...
                      </td>
                      <td className="px-4 py-3 text-red-600 font-bold">1.2s</td>
                      <td className="px-4 py-3 text-text-secondary dark:text-slate-400">10:42:05 AM</td>
                    </tr>
                    <tr className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-text-main dark:text-white truncate max-w-[200px]">
                        UPDATE orders SET status = 'processed'...
                      </td>
                      <td className="px-4 py-3 text-orange-600 font-bold">450ms</td>
                      <td className="px-4 py-3 text-text-secondary dark:text-slate-400">10:41:12 AM</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <figcaption className="px-4 py-2 bg-white dark:bg-slate-900 text-xs text-text-secondary dark:text-slate-400 border-t border-border-subtle dark:border-slate-700 text-center italic animate-fadeIn delay-250">
            Real-time visualization of MySQL performance metrics within Netraa console.
          </figcaption>
        </figure>
      </section>
      {/* MSSQL Monitoring Section */}
      <section id="mssql-monitoring" className="scroll-mt-24 mt-6 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="size-10 rounded-lg bg-[#a91d22]/10 dark:bg-[#a91d22]/20 flex items-center justify-center text-[#a91d22] transition-transform duration-300 hover:scale-105">
            <MdDns className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-text-main dark:text-white">MSSQL Monitoring</h2>
        </div>
        <p className="text-text-secondary dark:text-slate-400 mb-6 animate-fadeIn">
          Netraa provides dedicated support for Microsoft SQL Server, capturing specific counters such as Page Life Expectancy and Batch Requests/sec.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Memory Health Card */}
          <div className="bg-surface-light dark:bg-slate-900 p-6 rounded-xl border border-border-subtle dark:border-slate-700 shadow-sm relative overflow-hidden group hover:border-primary/30 transition-colors animate-fadeIn">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <span className="material-symbols-outlined text-6xl text-primary"></span>
            </div>
            <h3 className="text-lg font-bold text-text-main dark:text-white mb-4">Memory Health</h3>
            <div className="mb-4">
              <div className="flex justify-between text-xs font-bold mb-1">
                <span className="text-text-secondary dark:text-slate-400">Buffer Cache Hit Ratio</span>
                <span className="text-green-600">99.8%</span>
              </div>
              <div className="w-full bg-border-subtle dark:bg-slate-700 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: "99.8%" }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs font-bold mb-1">
                <span className="text-text-secondary dark:text-slate-400">Page Life Expectancy</span>
                <span className="text-text-main dark:text-white">420s</span>
              </div>
              <div className="w-full bg-border-subtle dark:bg-slate-700 rounded-full h-2">
                <div className="bg-primary h-2 rounded-full" style={{ width: "65%" }}></div>
              </div>
            </div>
          </div>

          {/* Active Connections Card */}
          <div className="bg-surface-light dark:bg-slate-900 p-6 rounded-xl border border-border-subtle dark:border-slate-700 shadow-sm flex flex-col justify-between group hover:border-primary/30 transition-colors animate-fadeIn delay-150">
            <div>
              <h3 className="text-lg font-bold text-text-main dark:text-white mb-1">Active Connections</h3>
              <p className="text-xs text-text-secondary dark:text-slate-400">Current sessions active on instance</p>
            </div>
            <div className="flex items-end gap-3 mt-4">
              <span className="text-5xl font-bold text-text-main dark:text-white tracking-tight">1,248</span>
              <span className="text-sm font-bold text-green-600 mb-2 flex items-center">
                <span className="material-symbols-outlined text-base">.</span> +2.4%
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Correlated Database Insights */}
      <section id="correlated-insights" className="scroll-mt-24 mt-6 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="size-10 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-dark transition-transform duration-300 hover:scale-105">
            <MdHub className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-text-main dark:text-white">Correlated Database Insights</h2>
        </div>
        <p className="text-text-secondary dark:text-slate-400 mb-4 animate-fadeIn">
          Netraa doesn't just monitor the database in isolation. It correlates database metrics with distributed traces from your application services.
        </p>
        <p className="text-text-secondary dark:text-slate-400 mb-6 animate-fadeIn delay-150">
          By linking a slow user transaction directly to the specific database lock or resource contention event that caused it, Netraa reduces Mean Time To Resolution (MTTR) significantly.
        </p>

        <div className="p-6 bg-surface-light dark:bg-slate-900 rounded-xl border border-border-subtle dark:border-slate-700 flex flex-col md:flex-row gap-6 items-center animate-fadeIn delay-200">
          <div className="flex-1">
            <h4 className="text-base font-bold text-text-main dark:text-white mb-2">From Trace to Query</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-green-500 dark:text-green-400 text-xl mt-0.5"><MdTrendingUp /></span>
                <span className="text-sm text-text-secondary dark:text-slate-400">View the exact SQL statement generated by your ORM.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-green-500 dark:text-green-400 text-xl mt-0.5"><MdCheckCircle /></span>
                <span className="text-sm text-text-secondary dark:text-slate-400">Identify N+1 query problems instantly in trace view.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-green-500 dark:text-green-400 text-xl mt-0.5"><MdCheckCircle /></span>
                <span className="text-sm text-text-secondary dark:text-slate-400">Correlate CPU spikes with batch jobs.</span>
              </li>
            </ul>
          </div>
          <div className="w-full md:w-1/3 bg-background-light dark:bg-slate-800 p-4 rounded-lg border border-border-subtle dark:border-slate-700 border-dashed flex items-center justify-center min-h-[140px] animate-fadeIn delay-250">
            <div className="text-center">
              <span className="material-symbols-outlined text-4xl text-text-secondary/30 dark:text-slate-400/30 mb-2"><MdAutoGraph /></span>
              <p className="text-xs text-text-secondary dark:text-slate-400">Trace Correlation Visual</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}